// Row interaction model (REQ-VIEW-014) — the pure rules behind the row behaviour shared across
// Today / Calendar / Tasks (design-direction-v3 §2, component-a11y-spec §4). No DOM, no I/O:
// it says WHICH affordances a row offers; render-see turns that into markup and main.ts wires it.
//
// The model in one line: tap/click the row OPENS it; the completion circle COMPLETES (todos only,
// and never opens); secondary actions (Reschedule · Delete) are revealed by swipe on touch or a
// kebab on pointer devices; DELETE is always behind a confirm/undo, never a bare button.
import type { ViewItem } from "./view";
import { isReadOnly, isTodo } from "./planner";

export type Pointer = "touch" | "pointer";
export type SecondaryAction = "reschedule" | "delete";

export interface RowModel {
  /** Tapping the row opens the item (its detail/edit sheet). Read-only rows open a detail view. */
  open: boolean;
  /** A completion circle — todos only (events have no circle). Read-only rows never complete. */
  complete: boolean;
  /** Secondary actions revealed behind swipe/kebab. Empty for read-only rows. */
  secondary: SecondaryAction[];
  /** How the secondary actions are reached: swipe on touch, a kebab menu on pointer devices. */
  reveal: "swipe" | "kebab";
}

/** The affordances a row offers, given the item and the pointer type. Pure. */
export function rowActions(it: ViewItem, opts: { pointer: Pointer }): RowModel {
  const readOnly = isReadOnly(it.source);
  return {
    open: true,
    complete: !readOnly && isTodo(it),
    secondary: readOnly ? [] : ["reschedule", "delete"],
    reveal: opts.pointer === "touch" ? "swipe" : "kebab",
  };
}

// ── Delete gate (REQ-VIEW-014) — a tiny reducer proving delete can't fire without a confirm ──
export type DeleteState = "idle" | "confirming" | "deleted";
export type DeleteEvent = "ask" | "confirm" | "cancel";

/** Delete is gated: idle → (ask) → confirming → (confirm) → deleted. There is no idle→deleted
 *  edge, so a single tap can never destroy a row; `cancel` returns to idle (the undo direction). */
export function deleteReducer(state: DeleteState, ev: DeleteEvent): DeleteState {
  if (state === "idle" && ev === "ask") return "confirming";
  if (state === "confirming" && ev === "confirm") return "deleted";
  if (state === "confirming" && ev === "cancel") return "idle";
  return state;
}
